/**
 * Ball-to-ball contact (PHY-003).
 *
 * Two impulses at the point where the balls touch:
 *
 *   • a NORMAL impulse with restitution, which is what transfers the shot, and
 *   • a TANGENTIAL impulse bounded by Coulomb friction, which is what produces
 *     cut-induced throw and spin transfer.
 *
 * The relative velocity is taken at the CONTACT POINT, including spin — the surfaces
 * are what rub together, not the centres. Without that there is no throw and no
 * English, and the game loses most of its depth.
 *
 * As everywhere in the stepping path: only + − × ÷ and Math.sqrt
 * (ARCHITECTURE.md §5.5).
 */
import type { PhysicsConfig } from './config.ts';
import type { TableState } from './state.ts';

/**
 * Earliest time within `maxT` at which balls `i` and `j` touch, or -1.
 *
 * Solves |d + w·t| = 2R for t, which is a quadratic. This is the swept test: a
 * discrete "are they overlapping now?" check would let a fast ball step straight over
 * another one.
 */
export function timeOfImpact(
  state: TableState,
  i: number,
  j: number,
  maxT: number,
  diameterM: number,
): number {
  const dx = (state.xM[j] ?? 0) - (state.xM[i] ?? 0);
  const dy = (state.yM[j] ?? 0) - (state.yM[i] ?? 0);
  const wx = (state.vxMps[j] ?? 0) - (state.vxMps[i] ?? 0);
  const wy = (state.vyMps[j] ?? 0) - (state.vyMps[i] ?? 0);

  const a = wx * wx + wy * wy;
  if (a === 0) return -1; // no relative motion: they can never meet

  const b = dx * wx + dy * wy;
  if (b >= 0) return -1; // separating, or moving in parallel

  const c = dx * dx + dy * dy - diameterM * diameterM;
  const discriminant = b * b - a * c;
  if (discriminant < 0) return -1; // they pass by without touching

  /*
   * Already touching or slightly overlapping (c <= 0) while still closing: resolve at
   * once rather than solving for a time in the past. This catches the rounding case
   * where a previous impulse left the pair a hair inside contact.
   */
  if (c <= 0) return 0;

  const t = (-b - Math.sqrt(discriminant)) / a;
  return t >= 0 && t <= maxT ? t : -1;
}

/**
 * Apply the contact impulses to balls `i` and `j`, which are assumed to be touching.
 * Returns the closing speed, for the event log.
 */
export function resolveBallContact(
  state: TableState,
  i: number,
  j: number,
  config: PhysicsConfig,
): number {
  const radiusM = config.ballRadiusM;

  let nx = (state.xM[j] ?? 0) - (state.xM[i] ?? 0);
  let ny = (state.yM[j] ?? 0) - (state.yM[i] ?? 0);
  const distance = Math.sqrt(nx * nx + ny * ny);
  if (distance === 0) return 0; // exactly coincident centres: no defined normal
  nx /= distance;
  ny /= distance;

  const vix = state.vxMps[i] ?? 0;
  const viy = state.vyMps[i] ?? 0;
  const vjx = state.vxMps[j] ?? 0;
  const vjy = state.vyMps[j] ?? 0;

  /*
   * Relative velocity of the two surfaces at the contact point:
   *
   *     u = (vᵢ − vⱼ) + R·(ωᵢ + ωⱼ) × n
   *
   * Only the in-plane part is used. The out-of-plane component — topspin rubbing
   * vertically at the contact — would need a vertical impulse, and a ball cannot leave
   * the cloth in a 2D-position model. See docs/physics.md deviation D4.
   */
  const spinZ = (state.wzRadS[i] ?? 0) + (state.wzRadS[j] ?? 0);
  const ux = vix - vjx + radiusM * -spinZ * ny;
  const uy = viy - vjy + radiusM * spinZ * nx;

  const un = ux * nx + uy * ny;
  if (un <= 0) return 0; // already separating

  // --- Normal impulse. Equal masses, so the reduced mass is m/2; m divides out.
  const jn = ((1 + config.ballBallRestitution) * un) / 2;

  state.vxMps[i] = vix - jn * nx;
  state.vyMps[i] = viy - jn * ny;
  state.vxMps[j] = vjx + jn * nx;
  state.vyMps[j] = vjy + jn * ny;

  // --- Tangential impulse, bounded by friction.
  const utx = ux - un * nx;
  const uty = uy - un * ny;
  const ut = Math.sqrt(utx * utx + uty * uty);

  if (ut > 0) {
    /*
     * A tangential impulse jt changes the relative surface velocity by 7·jt/m: 2/m
     * through the two centres, and 5/m through the two spins (I = (2/5)mR²). So
     * ut/7 is the impulse that would exactly stop the surfaces sliding.
     */
    const stopping = ut / 7;
    const limit = config.ballBallFriction * jn;
    const jt = stopping < limit ? stopping : limit;

    const tx = utx / ut;
    const ty = uty / ut;

    state.vxMps[i] = (state.vxMps[i] ?? 0) - jt * tx;
    state.vyMps[i] = (state.vyMps[i] ?? 0) - jt * ty;
    state.vxMps[j] = (state.vxMps[j] ?? 0) + jt * tx;
    state.vyMps[j] = (state.vyMps[j] ?? 0) + jt * ty;

    /*
     * The angular impulse is the same for both balls: ball i is pushed by −jt·t̂ at
     * +R·n from its centre, ball j by +jt·t̂ at −R·n, and the two torques come out
     * identical. For in-plane vectors the cross product is purely vertical, so this is
     * the spin transfer that carries English to the object ball.
     *
     * The sign matters and is easy to get backwards. A cue ball spinning clockwise
     * drags the object ball's contact surface with it, which spins the object ball
     * COUNTER-clockwise — adjacent gears turn opposite ways. Both balls receive the
     * same correction; they end up with opposite spins because the cue started with
     * one and the object with none.
     */
    const dOmegaZ = (-2.5 * jt * (nx * ty - ny * tx)) / radiusM;
    state.wzRadS[i] = (state.wzRadS[i] ?? 0) + dOmegaZ;
    state.wzRadS[j] = (state.wzRadS[j] ?? 0) + dOmegaZ;
  }

  return un;
}
